import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from 'config/axiosBaseQuery';
import { endpoints } from 'config/api';
import { Project, ProjectStatsResponse, PaginatedProjectsResponse, ProjectsQueryParams } from './projectsTypes';
import { AddSubtaskDependenciesRequest as SubtaskDependenciesRequest, CompleteSubtaskRequest } from './httpTypes';
import { sanitizeStagesData } from 'utils/projects';

export const projectsApi = createApi({
  reducerPath: 'projectsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Projects', 'Project', 'Budget', 'Documents', 'BudgetTemplate', 'Templates', 'Template'],
  endpoints: (builder) => ({
    fetchProjects: builder.query<PaginatedProjectsResponse, ProjectsQueryParams | void>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.limit) queryParams.append('limit', params.limit.toString());
        if (params?.view) queryParams.append('view', params.view);
        if (params?.search) queryParams.append('search', params.search);
        if (params?.status) queryParams.append('status', params.status);

        const queryString = queryParams.toString();
        return {
          url: `${endpoints.projects.base}${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['Projects'],
    }),

    getProjectsStats: builder.query<ProjectStatsResponse, void>({
      query: () => ({
        url: endpoints.projects.stats,
        method: 'GET',
        data: undefined,
        headers: undefined,
        params: undefined,
      }),
      providesTags: ['Projects'],
    }),

    getProject: builder.query<Project, string | undefined>({
      query: (projectId) => ({
        url: `${endpoints.projects.base}/${projectId}`,
        method: 'GET',
        data: undefined,
        headers: undefined,
        params: undefined,
        skip: !projectId,
      }),
      providesTags: (_res, _err, id) => [{ type: 'Project', id }],
    }),

    createProject: builder.mutation({
      query: (projectData) => {
        projectData.stages = sanitizeStagesData(projectData.stages);

        return {
          url: endpoints.projects.base,
          method: 'POST',
          data: projectData,
          headers: undefined,
          params: undefined
        }
      },
      invalidatesTags: ['Projects'],
    }),

    updateProject: builder.mutation<Project, any>({
      query: ({ projectId, updates }) => {
        // TODO: ESTO ES RE MIL PROVISORIO, JULITO POR FAVOR ARREGLALO :D
        // ESTO ES PORQUE LE ESTAN AGREGANDO UN PREFIX A LOS ID'S DE LAS TAREAS Y SUBTAREAS NUEVAS
        // EN EL FORMULARIO, HAY QUE REVISAR TODO ESE APPROACH
        if (updates.stages) {
          updates.stages = sanitizeStagesData(updates.stages);
        }

        return {
          url: `${endpoints.projects.base}/${projectId}`,
          method: 'PUT',
          data: updates,
          headers: {
            'Content-Type': 'application/json',
          },
          params: undefined
        };
      },
      invalidatesTags: (_res, _err, { projectId }) => [
        { type: 'Project', id: projectId },
        'Projects',
      ],
    }),

    deleteProject: builder.mutation<void, string>({
      query: (projectId) => ({
        url: `${endpoints.projects.base}/${projectId}`,
        method: 'DELETE',
        data: undefined,
        headers: undefined,
        params: undefined,
      }),
      invalidatesTags: ['Projects'],
    }),

    updateStage: builder.mutation<Project, {
      projectId: string;
      stageId: string;
      completed?: boolean;
      startDate?: string;
      endDate?: string;
      tasks?: Array<{
        _id?: string;
        name?: string;
        completed?: boolean;
        startDate?: string;
        endDate?: string;
        duration?: number;
        dependsOnPreviousTask?: boolean;
      }>;
    }>({
      query: ({ projectId, ...stageData }) => ({
        url: endpoints.projects.stage(projectId),
        method: 'PATCH',
        data: { projectId, ...stageData },
        headers: {
          'Content-Type': 'application/json',
        },
        params: undefined,
      }),
      invalidatesTags: (_res, _err, { projectId }) => [
        { type: 'Project', id: projectId },
        'Projects',
      ],
    }),

    updateProjectContacts: builder.mutation<any, { projectId: string; contacts: Array<{ name: string; lastName?: string; email: string; phone?: string }> }>({
      query: ({ projectId, contacts }) => ({
        url: `${endpoints.projects.base}/${projectId}/contacts`,
        method: 'PUT',
        data: { contacts },
        headers: {
          'Content-Type': 'application/json',
        },
        params: undefined,
      }),
      invalidatesTags: (_res, _err, { projectId }) => [
        { type: 'Project', id: projectId },
        'Projects',
      ],
    }),

    updateProjectConstructionInfo: builder.mutation<any, { projectId: string; constructionInfo: { initiatedBy?: string; updatedBy?: string; assignedTo?: string; startDate?: Date; endDate?: Date } }>({
      query: ({ projectId, constructionInfo }) => ({
        url: `${endpoints.projects.base}/${projectId}/construction-info`,
        method: 'PUT',
        data: constructionInfo,
        headers: {
          'Content-Type': 'application/json',
        },
        params: undefined,
      }),
      invalidatesTags: (_res, _err, { projectId }) => [
        { type: 'Project', id: projectId },
        'Projects',
      ],
    }),

    completeSubtask: builder.mutation<any, CompleteSubtaskRequest>({
      query: ({ projectId, ...body }) => ({
        url: `${endpoints.projects.base}/${projectId}/subtask/complete`,
        method: 'PATCH',
        data: body,
        headers: {
          'Content-Type': 'application/json',
        },
        params: undefined
      }),
      invalidatesTags: (_res, _err, { projectId }) => [
        { type: 'Project', id: projectId },
        'Projects',
      ],
    }),
    
    addSubtaskDependencies: builder.mutation<any, SubtaskDependenciesRequest>({
      query: ({ projectId, templateId, ...body }) => {
        if (projectId) {
          return {
            url: `${endpoints.projects.base}/${projectId}/dependencies`,
            method: 'POST',
            data: body,
            headers: {
              'Content-Type': 'application/json',
            },
            params: undefined
          };
        } else if (templateId) {
          return {
            url: `${endpoints.projectsTemplates.base}/${templateId}/dependencies`,
            method: 'POST',
            data: body,
            headers: {
              'Content-Type': 'application/json',
            },
            params: undefined
          };
        } else {
          throw new Error('Either projectId or templateId must be provided');
        }
      },
      invalidatesTags: (_res, _err, { projectId, templateId }) => {
        if (projectId) {
          return [
            { type: 'Project', id: projectId },
            'Projects',
          ];
        } else if (templateId) {
          return [
            { type: 'Template', id: templateId },
            'Templates',
          ];
        }
        return [];
      },
    }),

    removeSubtaskDependencies: builder.mutation<any, SubtaskDependenciesRequest>({
      query: ({ projectId, templateId, ...body }) => {
        if (projectId) {
          return {
            url: `${endpoints.projects.base}/${projectId}/dependencies`,
            method: 'DELETE',
            data: body,
            headers: {
              'Content-Type': 'application/json',
            },
            params: undefined
          };
        } else if (templateId) {
          return {
            url: `${endpoints.projectsTemplates.base}/${templateId}/dependencies`,
            method: 'DELETE',
            data: body,
            headers: {
              'Content-Type': 'application/json',
            },
            params: undefined
          };
        } else {
          throw new Error('Either projectId or templateId must be provided');
        }
      },
      invalidatesTags: (_res, _err, { projectId, templateId }) => {
        if (projectId) {
          return [
            { type: 'Project', id: projectId },
            'Projects',
          ];
        } else if (templateId) {
          return [
            { type: 'Template', id: templateId },
            'Templates',
          ];
        }
        return [];
      },
    }),


    getBudgetHistory: builder.query({
      query: (budgetId) => ({
        url: `${endpoints.budgets.base}/${budgetId}/history`,
        method: 'GET',
        data: undefined,
        params: undefined,
        headers: undefined,
      }),
    }),

    getBudgetDetails: builder.query({
      query: (budgetId) => ({
        url: endpoints.budgets.details(budgetId),
        method: 'GET',
        data: undefined,
        params: undefined,
        headers: undefined,
      }),
      providesTags: (_res, _err, id) => [{ type: 'Budget', id }],
    }),

    getBudgetSummary: builder.query({
      query: (budgetId) => ({
        url: `${endpoints.budgets.base}/${budgetId}/summary`,
        method: 'GET',
        data: undefined,
        params: undefined,
        headers: undefined,
      }),
      providesTags: (_res, _err, id) => [{ type: 'Budget', id }],
    }),

    uploadDocument: builder.mutation({
      query: ({ projectId, formData }) => ({
        url: `${endpoints.projects.documents(projectId)}/upload`,
        method: 'POST',
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' },
        params: undefined,
      }),
      invalidatesTags: ['Documents'],
    }),

    deleteDocument: builder.mutation({
      query: ({ projectId, documentId }) => ({
        url: `${endpoints.projects.documents(projectId)}/documents/${documentId}`,
        method: 'DELETE',
        data: undefined,
        params: undefined,
        headers: undefined,
      }),
      invalidatesTags: ['Documents'],
    }),

    getDocumentUrl: builder.query({
      query: ({ projectId, documentId }) => ({
        url: `${endpoints.projects.documents(projectId)}/documents/${documentId}/url`,
        method: 'GET',
        data: undefined,
        params: undefined,
        headers: undefined,
      }),
    }),

    toggleDocumentShare: builder.mutation({
      query: ({ projectId, documentId, shareWithCustomer }) => ({
        url: `${endpoints.projects.documents(projectId)}/documents/${documentId}/share`,
        method: 'PATCH',
        data: { shareWithCustomer },
        params: undefined,
        headers: undefined,
      }),
      invalidatesTags: (_res, _err, { projectId }) => [
        { type: 'Project', id: projectId },
        'Documents',
      ],
    }),

    addNote: builder.mutation<any, { projectId: string; content: string }>({
      query: ({ projectId, content }) => ({
        url: `${endpoints.projects.base}/${projectId}/notes`,
        method: 'POST',
        data: { content },
        params: undefined,
        headers: undefined,
      }),
      invalidatesTags: (_res, _err, { projectId }) => [
        { type: 'Project', id: projectId },
      ],
    }),

    assignUserToSubtask: builder.mutation({
      query: ({ projectId, data }) => ({
        url: `${endpoints.projects.base}/${projectId}/subtask/assign`,
        method: 'PATCH',
        data,
        params: undefined,
        headers: undefined,
      }),
      invalidatesTags: (_res, _err, { projectId }) => [
        { type: 'Project', id: projectId },
        'Projects',
      ],
    }),

    addItemGroup: builder.mutation({
      query: ({ budgetId, groupData }) => ({
        url: `${endpoints.budgets.base}/${budgetId}/item-groups`,
        method: 'POST',
        data: groupData,
        params: undefined,
        headers: undefined,
      }),
      invalidatesTags: ['Budget'],
    }),

    updateItemGroup: builder.mutation({
      query: ({ budgetId, groupId, groupData }) => ({
        url: `${endpoints.budgets.base}/${budgetId}/item-groups/${groupId}`,
        method: 'PUT',
        data: groupData,
        params: undefined,
        headers: undefined,
      }),
      invalidatesTags: ['Budget'],
    }),

    deleteItemGroup: builder.mutation({
      query: ({ budgetId, groupId }) => ({
        url: `${endpoints.budgets.base}/${budgetId}/item-groups/${groupId}`,
        method: 'DELETE',
        params: undefined,
        headers: undefined,
      }),
      invalidatesTags: ['Budget'],
    }),

    addJobItem: builder.mutation({
      query: ({ budgetId, groupId, itemData }) => ({
        url: `${endpoints.budgets.base}/${budgetId}/item-groups/${groupId}/job-items`,
        method: 'POST',
        data: itemData,
        params: undefined,
        headers: undefined,
      }),
      invalidatesTags: ['Budget'],
    }),

    updateJobItem: builder.mutation({
      query: ({ budgetId, groupId, itemId, itemData }) => ({
        url: `${endpoints.budgets.base}/${budgetId}/item-groups/${groupId}/job-items/${itemId}`,
        method: 'PUT',
        data: itemData,
        params: undefined,
        headers: undefined,
      }),
      invalidatesTags: ['Budget'],
    }),

    deleteJobItem: builder.mutation({
      query: ({ budgetId, groupId, itemId }) => ({
        url: `${endpoints.budgets.base}/${budgetId}/item-groups/${groupId}/job-items/${itemId}`,
        method: 'DELETE',
        params: undefined,
        headers: undefined,
      }),
      invalidatesTags: ['Budget'],
    }),

    updateMarkupItems: builder.mutation({
      query: ({ budgetId, markupItems }) => ({
        url: `${endpoints.budgets.base}/${budgetId}/markups`,
        method: 'PUT',
        data: { markupItems },
        params: undefined,
        headers: undefined,
      }),
      invalidatesTags: ['Budget'],
    }),

    createBudgetTemplate: builder.mutation({
      query: (templateData) => ({
        url: `${endpoints.budgets.base}/templates`,
        method: 'POST',
        data: templateData,
        params: undefined,
        headers: undefined,
      }),
      invalidatesTags: ['BudgetTemplate'],
    }),

    getBudgetTemplates: builder.query({
      query: () => ({
        url: `${endpoints.budgets.base}/templates`,
        method: 'GET',
        data: undefined,
        params: undefined,
        headers: undefined,
      }),
      providesTags: ['BudgetTemplate'],
    }),

    updateBudgetTemplate: builder.mutation({
      query: ({ templateId, templateData }) => ({
        url: `${endpoints.budgets.base}/templates/${templateId}`,
        method: 'PUT',
        data: templateData,
        params: undefined,
        headers: undefined,
      }),
      invalidatesTags: ['BudgetTemplate'],
    }),

    deleteBudgetTemplate: builder.mutation({
      query: (templateId) => ({
        url: `${endpoints.budgets.base}/templates/${templateId}`,
        method: 'DELETE',
        params: undefined,
        headers: undefined,
      }),
      invalidatesTags: ['BudgetTemplate'],
    }),

    // Schedule Activity endpoints
    createScheduleActivity: builder.mutation<
      any,
      { projectId: string; activity: Record<string, unknown> }
    >({
      query: ({ projectId, activity }) => ({
        url: `${endpoints.projects.base}/${projectId}/schedule-activities`,
        method: 'POST',
        data: activity,
        params: undefined,
        headers: undefined,
      }),
      invalidatesTags: (_res, _err, { projectId }) => [
        { type: 'Project', id: projectId },
        'Projects',
      ],
    }),

    updateScheduleActivity: builder.mutation<
      any,
      { projectId: string; activityId: string; updates: Record<string, unknown> }
    >({
      query: ({ projectId, activityId, updates }) => ({
        url: `${endpoints.projects.base}/${projectId}/schedule-activities/${activityId}`,
        method: 'PUT',
        data: updates,
        params: undefined,
        headers: undefined,
      }),
      invalidatesTags: (_res, _err, { projectId }) => [
        { type: 'Project', id: projectId },
        'Projects',
      ],
    }),

    deleteScheduleActivity: builder.mutation<
      any,
      { projectId: string; activityId: string }
    >({
      query: ({ projectId, activityId }) => ({
        url: `${endpoints.projects.base}/${projectId}/schedule-activities/${activityId}`,
        method: 'DELETE',
        data: undefined,
        params: undefined,
        headers: undefined,
      }),
      invalidatesTags: (_res, _err, { projectId }) => [
        { type: 'Project', id: projectId },
        'Projects',
      ],
    }),

    // Transfer tasks from one team member to another (team handoff)
    transferTasks: builder.mutation<
      {
        success: boolean;
        message: string;
        data: {
          subtasksTransferred: number;
          transferredSubtaskNames: string[];
          sourceUserId: string;
          targetUserId: string;
          sourceRemovedFromProject: boolean;
          notificationSent: boolean;
        };
      },
      {
        projectId: string;
        sourceUserId: string;
        targetUserId: string;
        notifyTarget?: boolean;
        removeSourceFromProject?: boolean;
      }
    >({
      query: ({ projectId, ...body }) => ({
        url: `${endpoints.projects.base}/${projectId}/transfer-tasks`,
        method: 'POST',
        data: body,
        headers: {
          'Content-Type': 'application/json',
        },
        params: undefined,
      }),
      invalidatesTags: (_res, _err, { projectId }) => [
        { type: 'Project', id: projectId },
        'Projects',
      ],
    }),
  }),
});

export const {
  useFetchProjectsQuery,
  useGetProjectsStatsQuery,
  useGetProjectQuery,
  useCreateProjectMutation,
  useUpdateProjectMutation,
  useUpdateProjectContactsMutation,
  useUpdateProjectConstructionInfoMutation,
  useDeleteProjectMutation,
  useCompleteSubtaskMutation,
  useGetBudgetHistoryQuery,
  useGetBudgetDetailsQuery,
  useUploadDocumentMutation,
  useDeleteDocumentMutation,
  useGetDocumentUrlQuery,
  useLazyGetDocumentUrlQuery,
  useToggleDocumentShareMutation,
  useAddNoteMutation,
  useAssignUserToSubtaskMutation,
  useAddSubtaskDependenciesMutation,
  useRemoveSubtaskDependenciesMutation,
  useGetBudgetSummaryQuery,
  useAddItemGroupMutation,
  useUpdateItemGroupMutation,
  useDeleteItemGroupMutation,
  useAddJobItemMutation,
  useUpdateJobItemMutation,
  useDeleteJobItemMutation,
  useUpdateMarkupItemsMutation,
  useCreateBudgetTemplateMutation,
  useGetBudgetTemplatesQuery,
  useUpdateBudgetTemplateMutation,
  useDeleteBudgetTemplateMutation,
  useTransferTasksMutation,
  useCreateScheduleActivityMutation,
  useUpdateScheduleActivityMutation,
  useDeleteScheduleActivityMutation,
  useUpdateStageMutation,
} = projectsApi;
